import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  TextInput,
} from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { contactAdminAPI } from '../../services/api';
import { Logger } from '../../utils/logger';

interface Props {
  visible: boolean;
  onClose: () => void;
}

// "Contact Admin" form opened from the dashboard tile. The message is emailed
// to the admin address the school configured under Notification Settings; the
// backend attaches the parent's name, email and children automatically.
export function ContactAdminModal({ visible, onClose }: Props) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setSubject('');
    setMessage('');
    setSent(false);
    setError(null);
    setSending(false);
  };

  const handleClose = () => {
    if (sending) return;
    reset();
    onClose();
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      setError('Enter a subject.');
      return;
    }
    if (!message.trim()) {
      setError('Enter a message.');
      return;
    }
    setError(null);
    setSending(true);
    try {
      await contactAdminAPI.send(subject.trim(), message.trim());
      setSent(true);
    } catch (err: any) {
      Logger.error('ContactAdmin', 'Failed to send message', err);
      setError(
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        'Could not send your message. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          {sent ? (
            <View style={styles.sentBox}>
              <View style={styles.sentIconCircle}>
                <FeatherIcon name="check" size={26} color="#ECFDF5" />
              </View>
              <Text style={styles.sentTitle}>Message sent</Text>
              <Text style={styles.sentSubtitle}>
                The school office will get back to you by email.
              </Text>
              <TouchableOpacity style={styles.sendBtn} onPress={handleClose}>
                <Text style={styles.sendBtnText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <View style={styles.header}>
                <View style={styles.headerLeft}>
                  <FeatherIcon name="mail" size={19} color="#C7D2FE" />
                  <Text style={styles.headerTitle}>Contact Admin</Text>
                </View>
                <TouchableOpacity onPress={handleClose} disabled={sending}>
                  <FeatherIcon name="x" size={20} color="#C7D2FE" />
                </TouchableOpacity>
              </View>

              <View style={styles.body}>
                <View style={styles.infoBanner}>
                  <FeatherIcon name="info" size={15} color="#1D4ED8" style={styles.infoIcon} />
                  <Text style={styles.infoText}>
                    Sent to the school office by email, with your name and your
                    child's details attached.
                  </Text>
                </View>

                <Text style={styles.label}>Subject</Text>
                <TextInput
                  style={styles.input}
                  placeholder="What is this about?"
                  placeholderTextColor="#9CA3AF"
                  value={subject}
                  onChangeText={(t) => { setSubject(t); setError(null); }}
                  editable={!sending}
                  maxLength={150}
                />

                <Text style={styles.label}>Message</Text>
                <TextInput
                  style={[styles.input, styles.messageInput]}
                  placeholder="Write your message to the school office…"
                  placeholderTextColor="#9CA3AF"
                  value={message}
                  onChangeText={(t) => { setMessage(t); setError(null); }}
                  editable={!sending}
                  multiline
                  numberOfLines={5}
                  maxLength={4000}
                  textAlignVertical="top"
                />

                {error && <Text style={styles.errorText}>{error}</Text>}

                <View style={styles.footer}>
                  <TouchableOpacity style={styles.cancelBtn} onPress={handleClose} disabled={sending}>
                    <Text style={styles.cancelBtnText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.sendBtn, sending && styles.sendBtnDisabled]}
                    onPress={handleSend}
                    disabled={sending}
                  >
                    {sending ? (
                      <ActivityIndicator size="small" color="#EEF2FF" />
                    ) : (
                      <>
                        <FeatherIcon name="send" size={15} color="#EEF2FF" style={styles.sendIcon} />
                        <Text style={styles.sendBtnText}>Send message</Text>
                      </>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    width: '100%',
    maxWidth: 440,
    overflow: 'hidden',
  },
  header: {
    backgroundColor: '#4F46E5',
    paddingHorizontal: 18,
    paddingVertical: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  headerTitle: { color: '#EEF2FF', fontSize: 16, fontWeight: '600' },
  body: { paddingHorizontal: 18, paddingVertical: 16 },
  infoBanner: {
    flexDirection: 'row',
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
    marginBottom: 14,
  },
  infoIcon: { marginTop: 2, marginRight: 8 },
  infoText: { flex: 1, fontSize: 12.5, color: '#1E40AF', lineHeight: 17 },
  label: { fontSize: 13, color: '#6B7280', marginBottom: 4 },
  input: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 14,
    color: '#111827',
    marginBottom: 12,
    backgroundColor: '#FFFFFF',
  },
  messageInput: { minHeight: 110 },
  errorText: { color: '#DC2626', fontSize: 13, marginBottom: 8 },
  footer: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 4 },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  cancelBtnText: { color: '#374151', fontSize: 14, fontWeight: '500' },
  sendBtn: {
    backgroundColor: '#4F46E5',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendBtnDisabled: { opacity: 0.7 },
  sendIcon: { marginRight: 7 },
  sendBtnText: { color: '#EEF2FF', fontSize: 14, fontWeight: '600' },
  sentBox: { alignItems: 'center', paddingHorizontal: 24, paddingVertical: 28 },
  sentIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#059669',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  sentTitle: { fontSize: 16, fontWeight: '600', color: '#064E3B', marginBottom: 4 },
  sentSubtitle: { fontSize: 13, color: '#047857', textAlign: 'center', marginBottom: 18 },
});
